<template>
  <div class="page-card manage-page">
    <div class="page-header">
      <div>
        <h2 class="page-title">通告招募管理</h2>
        <div v-if="notice.title" class="page-subtitle">{{ notice.title }} · {{ notice.publishSourceText || '-' }}</div>
      </div>
      <div>
        <el-button @click="goDetail">查看通告详情</el-button>
        <el-button @click="goBack">返回大厅</el-button>
      </div>
    </div>

    <el-tabs v-model="activeTab" @tab-click="handleTabChange">
      <el-tab-pane name="applications">
        <span slot="label" class="tab-label-wrap">
          报名管理
          <span v-if="applicationBadgeCount" class="tab-badge">{{ badgeValue(applicationBadgeCount) }}</span>
        </span>
        <div class="toolbar">
          <el-select v-model="applicationQuery.applyStatus" clearable placeholder="报名状态" style="width: 140px">
            <el-option v-for="item in applicationStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <el-input v-model="applicationQuery.keyword" clearable placeholder="艺人/留言关键字" style="width: 220px" />
          <el-button type="primary" @click="fetchApplications">查询</el-button>
        </div>
        <el-table :data="applications" border v-loading="applicationLoading">
          <el-table-column prop="applicationNo" label="报名编号" min-width="160" />
          <el-table-column prop="artistName" label="艺人" min-width="120" />
          <el-table-column prop="city" label="城市" width="100" />
          <el-table-column label="状态" width="110">
            <template slot-scope="{ row }">{{ applicationStatusLabel(row.applyStatus) }}</template>
          </el-table-column>
          <el-table-column prop="createdAt" label="报名时间" width="170" />
          <el-table-column label="操作" width="220" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" @click="openApplicationDetail(row)">详情</el-button>
              <el-button v-if="canHandleApplication(row)" type="text" @click="updateApplicationStatus(row, 6)">录用</el-button>
              <el-button v-if="canHandleApplication(row)" type="text" class="danger-link" @click="updateApplicationStatus(row, 7)">拒绝</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane name="invitations">
        <span slot="label" class="tab-label-wrap">
          艺人邀约
        </span>
        <div class="toolbar">
          <el-input v-model="artistQuery.keyword" clearable placeholder="搜索艺人" style="width: 220px" />
          <el-button type="primary" @click="fetchArtists">搜索艺人</el-button>
        </div>
        <el-table :data="artists" border v-loading="artistLoading" class="section-table">
          <el-table-column prop="artistName" label="艺人" min-width="120" />
          <el-table-column prop="city" label="城市" width="100" />
          <el-table-column label="性别" width="80">
            <template slot-scope="{ row }">{{ genderText(row.gender) }}</template>
          </el-table-column>
          <el-table-column prop="age" label="年龄" width="80" />
          <el-table-column label="操作" width="160">
            <template slot-scope="{ row }">
              <el-button v-if="row.canInvite === true" type="text" @click="openInviteDialog(row)">发起邀约</el-button>
              <el-tooltip v-else :content="row.inviteBlockMessage || '当前无法发起邀约'" placement="top">
                <span class="invite-disabled-link">不可邀约</span>
              </el-tooltip>
            </template>
          </el-table-column>
        </el-table>

        <div class="section-title">邀约记录</div>
        <el-table :data="invitations" border v-loading="invitationLoading">
          <el-table-column prop="invitationNo" label="邀约编号" min-width="160" />
          <el-table-column prop="artistName" label="艺人" min-width="120" />
          <el-table-column prop="invitationTitle" label="标题" min-width="160" />
          <el-table-column label="状态" width="110">
            <template slot-scope="{ row }">{{ row.statusText || invitationStatusLabel(row.status) }}</template>
          </el-table-column>
          <el-table-column prop="createdAt" label="创建时间" width="170" />
          <el-table-column label="操作" width="120">
            <template slot-scope="{ row }">
              <el-button v-if="Number(row.status) === 1" type="text" class="danger-link" @click="cancelInvitation(row)">撤回</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane name="orders">
        <span slot="label" class="tab-label-wrap">
          关联订单
          <span v-if="orderBadgeCount" class="tab-badge">{{ badgeValue(orderBadgeCount) }}</span>
        </span>
        <el-table :data="orders" border v-loading="orderLoading">
          <el-table-column label="订单编号" min-width="190">
            <template slot-scope="{ row }">
              <span class="order-no-wrap">
                {{ row.orderNo || '-' }}
                <span v-if="isPendingOrder(row)" class="order-row-badge">+1</span>
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="artistName" label="艺人" min-width="120" />
          <el-table-column prop="sourceText" label="来源" width="120" />
          <el-table-column prop="fulfillmentStatusText" label="履约状态" width="110" />
          <el-table-column prop="settlementStatusText" label="结算状态" width="110" />
          <el-table-column prop="createdAt" label="创建时间" width="170" />
          <el-table-column label="操作" width="340" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" @click="openOrderDetail(row)">详情</el-button>
              <el-button
                v-if="hasCloseRequest(row)"
                type="text"
                class="danger-link"
                @click="openCloseRequestDialog(row)"
              >异常</el-button>
              <el-button v-if="Number(row.fulfillmentStatus) === 2" type="text" @click="updateFulfillment(row, 3)">已验收</el-button>
              <el-button v-if="canSettle(row)" type="text" @click="openSettlementDialog(row)">结算</el-button>
              <el-button v-if="row.canReviewArtist" type="text" @click="openReviewDialog(row)">评价艺人</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <el-dialog
      title="报名详情"
      :visible.sync="applicationDetailVisible"
      width="1040px"
      top="5vh"
      @closed="resetApplicationDetailDialog"
    >
      <div v-loading="applicationDetailLoading" class="application-detail-dialog">
        <template v-if="applicationDetail.id">
          <div class="section-title dialog-section-title">报名信息</div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="报名编号">{{ applicationDetail.applicationNo || '-' }}</el-descriptions-item>
            <el-descriptions-item label="报名状态">{{ applicationStatusLabel(applicationDetail.applyStatus) }}</el-descriptions-item>
            <el-descriptions-item label="艺人">{{ applicationDetail.artistName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="报名时间">{{ applicationDetail.createdAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="城市">{{ applicationDetail.city || '-' }}</el-descriptions-item>
            <el-descriptions-item label="查看时间">{{ applicationDetail.viewedAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="留言" :span="2">{{ applicationDetail.applyMessage || '-' }}</el-descriptions-item>
            <el-descriptions-item label="商家备注" :span="2">{{ applicationDetail.merchantRemark || '-' }}</el-descriptions-item>
          </el-descriptions>

          <el-divider>艺人资料</el-divider>
          <div class="artist-summary">
            <el-image
              v-if="applicationProfile.avatarUrl"
              :src="resolveMediaUrl(applicationProfile.avatarUrl)"
              fit="cover"
              class="artist-avatar-large"
            />
            <div v-else class="artist-avatar-large artist-avatar-empty">头像</div>
            <div class="artist-summary-meta">
              <div class="artist-summary-name">{{ applicationProfile.artistName || '-' }}</div>
              <div class="artist-summary-sub">{{ applicationProfile.nickname || '未填写昵称' }}</div>
              <div class="artist-summary-tags">
                <el-tag size="small">{{ genderText(applicationProfile.gender) }}</el-tag>
                <el-tag size="small" type="info">{{ applicationProfile.city || '未填写城市' }}</el-tag>
                <el-tag size="small" :type="membershipTagType(applicationProfile)">{{ membershipText(applicationProfile) }}</el-tag>
              </div>
            </div>
          </div>
          <el-descriptions :column="2" border>
            <el-descriptions-item label="艺名">{{ applicationProfile.artistName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="昵称">{{ applicationProfile.nickname || '-' }}</el-descriptions-item>
            <el-descriptions-item label="联系手机">{{ maskMobile(applicationProfile.contactMobile) }}</el-descriptions-item>
            <el-descriptions-item label="会员到期">{{ membershipExpireText(applicationProfile) }}</el-descriptions-item>
            <el-descriptions-item label="性别">{{ genderText(applicationProfile.gender) }}</el-descriptions-item>
            <el-descriptions-item label="年龄">{{ applicationProfile.age || '-' }}</el-descriptions-item>
            <el-descriptions-item label="身高">{{ applicationProfile.heightCm || '-' }}</el-descriptions-item>
            <el-descriptions-item label="体重">{{ applicationProfile.weightKg || '-' }}</el-descriptions-item>
            <el-descriptions-item label="技能标签" :span="2">{{ applicationProfile.skillTags || '-' }}</el-descriptions-item>
            <el-descriptions-item label="通告偏好" :span="2">{{ applicationProfile.noticeTypes || '-' }}</el-descriptions-item>
            <el-descriptions-item label="经验描述" :span="2">{{ applicationProfile.experienceDesc || '-' }}</el-descriptions-item>
            <el-descriptions-item label="档期说明" :span="2">{{ applicationProfile.scheduleDesc || '-' }}</el-descriptions-item>
          </el-descriptions>

          <el-divider>照片资料</el-divider>
          <div v-if="applicationImageMediaList.length" class="media-grid">
            <el-image
              v-for="item in applicationImageMediaList"
              :key="item.id || item.mediaUrl"
              :src="item.mediaUrl"
              :preview-src-list="applicationPreviewImages"
              fit="cover"
              class="media-image"
            />
          </div>
          <el-empty v-else description="暂无照片资料" :image-size="88" />

          <el-divider>视频资料</el-divider>
          <div v-if="applicationVideoMediaList.length" class="media-grid video-grid">
            <div v-for="item in applicationVideoMediaList" :key="item.id || item.mediaUrl" class="video-card">
              <video
                class="media-video"
                :src="item.mediaUrl"
                :poster="item.coverUrl || ''"
                controls
                preload="metadata"
              />
              <div class="video-actions">
                <el-button size="mini" type="primary" plain @click="openVideoPreview(item)">放大预览</el-button>
              </div>
            </div>
          </div>
          <el-empty v-else description="暂无视频资料" :image-size="88" />

          <el-divider>报名/邀约记录</el-divider>
          <el-table v-if="applicationHistoryRecords.length" :data="applicationHistoryRecords" border class="history-table">
            <el-table-column prop="recordType" label="类型" width="110" />
            <el-table-column prop="recordTitle" label="标题" min-width="220" />
            <el-table-column label="状态" width="120">
              <template slot-scope="{ row }">{{ historyStatusText(row) }}</template>
            </el-table-column>
            <el-table-column prop="createdAt" label="时间" width="180" />
          </el-table>
          <el-empty v-else description="暂无报名或邀约记录" :image-size="88" />

          <el-divider>艺人IP档案</el-divider>
          <div class="stat-grid">
            <div v-for="item in applicationStarOrderStatItems" :key="item.label" class="stat-item">
              <div class="stat-value">{{ item.value }}</div>
              <div class="stat-label">{{ item.label }}</div>
            </div>
          </div>

          <el-table v-if="applicationStarRecords.length" :data="applicationStarRecords" border class="star-table">
            <el-table-column label="类型" width="110">
              <template slot-scope="{ row }">{{ row.recordTypeText || recordTypeText(row.recordType) }}</template>
            </el-table-column>
            <el-table-column prop="recordTitle" label="标题" min-width="150" />
            <el-table-column prop="eventDate" label="日期" width="120" />
            <el-table-column prop="partnerName" label="机构/项目方" min-width="140" />
            <el-table-column prop="roleName" label="角色/身份" min-width="120" />
            <el-table-column label="描述" min-width="220" show-overflow-tooltip>
              <template slot-scope="{ row }">{{ starRecordDesc(row) }}</template>
            </el-table-column>
            <el-table-column label="附件" min-width="220">
              <template slot-scope="{ row }">
                <div v-if="starAttachmentItems(row).length" class="star-attachment-grid">
                  <div
                    v-for="(item, index) in starAttachmentItems(row)"
                    :key="`${item.mediaUrl}-${index}`"
                    class="star-attachment-item"
                  >
                    <el-image
                      v-if="isImageAttachment(item)"
                      :src="item.mediaUrl"
                      :preview-src-list="starImagePreviewList(row)"
                      fit="cover"
                      class="star-attachment-thumb"
                    >
                      <div slot="error" class="star-attachment-error">图片加载失败</div>
                    </el-image>
                    <div v-else-if="isVideoAttachment(item)" class="star-attachment-video-card" @click="openVideoPreview(item)">
                      <video class="star-attachment-video" :src="item.mediaUrl" :poster="item.coverUrl || ''" muted preload="metadata" />
                      <div class="star-attachment-video-mask">
                        <span class="star-attachment-video-play">▶</span>
                        <span>视频</span>
                      </div>
                    </div>
                    <el-link v-else :href="item.mediaUrl" target="_blank" type="primary" class="star-attachment-file">查看附件</el-link>
                  </div>
                </div>
                <span v-else>-</span>
              </template>
            </el-table-column>
          </el-table>
          <el-empty v-else description="暂无艺人IP档案" :image-size="88" />
        </template>
      </div>
    </el-dialog>

    <el-dialog title="发起邀约" :visible.sync="inviteDialogVisible" width="520px" @closed="resetInviteDialog">
      <el-form label-width="90px">
        <el-form-item label="艺人">
          <span>{{ inviteForm.artistName || '-' }}</span>
        </el-form-item>
        <el-form-item label="邀约标题" required>
          <el-input v-model="inviteForm.invitationTitle" placeholder="请输入邀约标题" />
        </el-form-item>
        <el-form-item label="邀约内容">
          <el-input v-model="inviteForm.invitationContent" type="textarea" :rows="4" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="inviteDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="inviteSubmitting" @click="submitInvitation">确认邀约</el-button>
      </span>
    </el-dialog>

    <el-dialog title="订单结算" :visible.sync="settlementDialogVisible" width="480px" @closed="resetSettlementDialog">
      <el-form label-width="100px">
        <el-form-item label="订单编号">
          <span>{{ settlementForm.orderNo || '-' }}</span>
        </el-form-item>
        <el-form-item label="结算金额" required>
          <el-input v-model="settlementForm.settlementAmount" placeholder="请输入结算金额" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="settlementForm.remark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="settlementDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="settlementSubmitting" @click="submitSettlement">确认结算</el-button>
      </span>
    </el-dialog>

    <el-dialog title="评价艺人" :visible.sync="reviewDialogVisible" width="520px" @closed="resetReviewDialog">
      <el-form label-width="90px">
        <el-form-item label="订单编号">
          <span>{{ reviewForm.orderNo || '-' }}</span>
        </el-form-item>
        <el-form-item label="艺人">
          <span>{{ reviewForm.artistName || '-' }}</span>
        </el-form-item>
        <el-form-item label="评价内容" required>
          <el-input
            v-model="reviewForm.content"
            type="textarea"
            :rows="4"
            maxlength="300"
            show-word-limit
            placeholder="请输入评价内容，仅可评价一次"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="reviewDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="reviewSubmitting" @click="submitReview">确认评价</el-button>
      </span>
    </el-dialog>

    <el-dialog title="订单详情" :visible.sync="orderDetailVisible" width="960px" top="5vh" @closed="resetOrderDetailDialog">
      <div v-loading="orderDetailLoading" class="order-detail-wrap">
        <template v-if="orderDetail.id">
          <el-descriptions title="合作信息" :column="2" border class="detail-section">
            <el-descriptions-item label="订单编号">{{ orderDetail.orderNo || '-' }}</el-descriptions-item>
            <el-descriptions-item label="创建时间">{{ orderDetail.createdAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="通告标题" :span="2">{{ orderDetail.noticeTitle || '-' }}</el-descriptions-item>
            <el-descriptions-item label="来源类型">{{ orderDetail.sourceText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="来源编号">{{ orderDetail.sourceBizNo || '-' }}</el-descriptions-item>
            <el-descriptions-item label="活动开始">{{ orderDetail.activityStartAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="活动结束">{{ orderDetail.activityEndAt || '-' }}</el-descriptions-item>
            <el-descriptions-item label="合作城市">{{ orderLocationText }}</el-descriptions-item>
            <el-descriptions-item label="详细地点">{{ orderDetail.locationDetail || '-' }}</el-descriptions-item>
            <el-descriptions-item label="费用说明" :span="2">{{ orderDetail.salaryDesc || '-' }}</el-descriptions-item>
            <el-descriptions-item label="履约状态">{{ orderDetail.fulfillmentStatusText || '-' }}</el-descriptions-item>
            <el-descriptions-item label="结算状态">{{ orderDetail.settlementStatusText || '-' }}</el-descriptions-item>
            <el-descriptions-item v-if="orderDetail.closeReason" label="关闭原因" :span="2">{{ orderDetail.closeReason }}</el-descriptions-item>
          </el-descriptions>

          <div class="detail-section">
            <div class="section-title">合作艺人信息</div>
            <div class="artist-summary">
              <el-image
                v-if="orderArtistProfile.avatarUrl"
                :src="resolveMediaUrl(orderArtistProfile.avatarUrl)"
                fit="cover"
                class="artist-avatar-large"
              />
              <div v-else class="artist-avatar-large artist-avatar-empty">头像</div>
              <div class="artist-summary-meta">
                <div class="artist-summary-name">{{ orderArtistProfile.artistName || orderDetail.artistName || '-' }}</div>
                <div class="artist-summary-sub">{{ orderArtistProfile.nickname || '未填写昵称' }}</div>
                <div class="artist-summary-tags">
                  <el-tag size="small">{{ genderText(orderArtistProfile.gender) }}</el-tag>
                  <el-tag size="small" type="info">{{ orderArtistProfile.city || '未填写城市' }}</el-tag>
                  <el-tag size="small" :type="membershipTagType(orderArtistProfile)">{{ membershipText(orderArtistProfile) }}</el-tag>
                </div>
              </div>
            </div>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="商家名称">{{ orderMerchantDisplayName }}</el-descriptions-item>
              <el-descriptions-item label="主体商家">{{ orderSubjectMerchantName }}</el-descriptions-item>
              <el-descriptions-item label="艺名">{{ orderArtistProfile.artistName || orderDetail.artistName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="昵称">{{ orderArtistProfile.nickname || '-' }}</el-descriptions-item>
              <el-descriptions-item label="联系手机">{{ maskMobile(orderArtistProfile.contactMobile) }}</el-descriptions-item>
              <el-descriptions-item label="报名编号">{{ orderDetail.applicationNo || '-' }}</el-descriptions-item>
              <el-descriptions-item label="邀约编号">{{ orderDetail.invitationNo || '-' }}</el-descriptions-item>
              <el-descriptions-item label="性别">{{ genderText(orderArtistProfile.gender) }}</el-descriptions-item>
              <el-descriptions-item label="年龄">{{ orderArtistProfile.age || '-' }}</el-descriptions-item>
              <el-descriptions-item label="身高">{{ orderArtistProfile.heightCm || '-' }}</el-descriptions-item>
              <el-descriptions-item label="体重">{{ orderArtistProfile.weightKg || '-' }}</el-descriptions-item>
              <el-descriptions-item label="技能标签" :span="2">{{ orderArtistProfile.skillTags || '-' }}</el-descriptions-item>
              <el-descriptions-item label="通告偏好" :span="2">{{ orderArtistProfile.noticeTypes || '-' }}</el-descriptions-item>
            </el-descriptions>

            <el-divider>形象照与照片</el-divider>
            <div v-if="orderArtistImageMediaList.length" class="media-grid">
              <el-image
                v-for="item in orderArtistImageMediaList"
                :key="item.id || item.mediaUrl"
                :src="item.mediaUrl"
                :preview-src-list="orderArtistPreviewImages"
                fit="cover"
                class="media-image"
              />
            </div>
            <el-empty v-else description="暂无形象照或照片" :image-size="88" />

            <el-divider>视频资料</el-divider>
            <div v-if="orderArtistVideoMediaList.length" class="media-grid video-grid">
              <div v-for="item in orderArtistVideoMediaList" :key="item.id || item.mediaUrl" class="video-card">
                <video
                  class="media-video"
                  :src="item.mediaUrl"
                  :poster="item.coverUrl || ''"
                  controls
                  preload="metadata"
                />
                <div class="video-actions">
                  <el-button size="mini" type="primary" plain @click="openVideoPreview(item)">放大预览</el-button>
                </div>
              </div>
            </div>
            <el-empty v-else description="暂无视频资料" :image-size="88" />
          </div>

          <div class="detail-section">
            <el-divider>订单图片</el-divider>
            <div v-if="orderDetailMediaList.length">
              <div class="media-tag-list">
                <el-tag
                  v-for="item in orderDetailMediaList"
                  :key="item.id || item.mediaUrl"
                  :type="mediaTagType(item.mediaTypeText)"
                  size="small"
                >
                  {{ item.mediaTypeText || '未分类' }}
                </el-tag>
              </div>
              <div v-if="orderDetailImageMediaList.length" class="media-grid">
                <div v-for="item in orderDetailImageMediaList" :key="item.id || item.mediaUrl" class="detail-media-card">
                  <el-image
                    :src="item.mediaUrl"
                    :preview-src-list="orderDetailPreviewImages"
                    fit="cover"
                    class="media-image"
                  />
                  <div class="detail-media-meta">
                    <el-tag size="mini" :type="mediaTagType(item.mediaTypeText)">{{ item.mediaTypeText || '未分类' }}</el-tag>
                    <span>{{ item.remark || '无备注' }}</span>
                  </div>
                </div>
              </div>
              <el-empty v-else description="暂无图片资料" :image-size="88" />
              <el-divider v-if="orderDetailVideoMediaList.length">视频资料</el-divider>
              <div v-if="orderDetailVideoMediaList.length" class="media-grid video-grid">
                <div v-for="item in orderDetailVideoMediaList" :key="item.id || item.mediaUrl" class="video-card">
                  <video
                    class="media-video"
                    :src="item.mediaUrl"
                    :poster="item.coverUrl || ''"
                    controls
                    preload="metadata"
                  />
                  <div class="detail-media-meta">
                    <el-tag size="mini" :type="mediaTagType(item.mediaTypeText)">{{ item.mediaTypeText || '未分类' }}</el-tag>
                    <span>{{ item.remark || '无备注' }}</span>
                  </div>
                  <div class="video-actions">
                    <el-button size="mini" type="primary" plain @click="openVideoPreview(item)">放大预览</el-button>
                  </div>
                </div>
              </div>
            </div>
            <el-empty v-else description="暂无订单图片" :image-size="88" />
          </div>

          <div class="detail-section">
            <div class="section-title">合作评价</div>
            <div class="review-grid">
              <div class="review-card">
                <div class="review-title">商家评价</div>
                <div v-if="orderDetail.merchantReview">
                  <div class="review-meta">
                    <span>{{ orderDetail.merchantReview.operatorName || '-' }}</span>
                    <span>{{ orderDetail.merchantReview.createdAt || '-' }}</span>
                  </div>
                  <div class="review-content">{{ orderDetail.merchantReview.content || '-' }}</div>
                </div>
                <el-empty v-else description="商家暂未评价" :image-size="68" />
              </div>
              <div class="review-card">
                <div class="review-title">艺人评价</div>
                <div v-if="orderDetail.artistReview">
                  <div class="review-meta">
                    <span>{{ orderDetail.artistReview.operatorName || '-' }}</span>
                    <span>{{ orderDetail.artistReview.createdAt || '-' }}</span>
                  </div>
                  <div class="review-content">{{ orderDetail.artistReview.content || '-' }}</div>
                </div>
                <el-empty v-else description="艺人暂未评价" :image-size="68" />
              </div>
            </div>
          </div>

          <div class="detail-section">
            <div class="section-title">状态时间线</div>
            <el-timeline v-if="orderDetail.logs && orderDetail.logs.length">
              <el-timeline-item v-for="item in orderDetail.logs" :key="item.id" :timestamp="item.createdAt">
                <div class="log-item-title">{{ item.remark || '订单状态更新' }}</div>
                <div class="log-item-desc">{{ item.operatorName || '系统' }}</div>
              </el-timeline-item>
            </el-timeline>
            <el-empty v-else description="暂无状态记录" :image-size="88" />
          </div>
        </template>
      </div>
    </el-dialog>

    <el-dialog title="异常关闭申请" :visible.sync="closeRequestDialogVisible" width="520px" @closed="resetCloseRequestDialog">
      <el-descriptions :column="1" border>
        <el-descriptions-item label="订单编号">{{ closeRequestForm.orderNo || '-' }}</el-descriptions-item>
        <el-descriptions-item label="艺人">{{ closeRequestForm.artistName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="申请时间">{{ closeRequestForm.closeRequestAt || '-' }}</el-descriptions-item>
        <el-descriptions-item label="申请理由">{{ closeRequestForm.closeRequestReason || '-' }}</el-descriptions-item>
      </el-descriptions>
      <el-form label-width="90px" class="close-request-reject-form">
        <el-form-item label="拒绝原因">
          <el-input
            v-model="closeRequestForm.rejectReason"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="拒绝时请输入原因"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="closeRequestDialogVisible = false">取消</el-button>
        <el-button type="danger" plain :loading="closeRequestSubmitting" @click="rejectCloseRequest">拒绝</el-button>
        <el-button type="primary" :loading="closeRequestSubmitting" @click="approveCloseRequest">同意关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="视频预览"
      :visible.sync="videoPreviewVisible"
      width="960px"
      top="6vh"
      append-to-body
      @closed="handleVideoPreviewClosed"
    >
      <div class="video-preview-wrap">
        <video
          v-if="previewVideo.mediaUrl"
          ref="previewVideoPlayer"
          class="preview-video-player"
          :src="previewVideo.mediaUrl"
          :poster="previewVideo.coverUrl || ''"
          controls
          autoplay
          preload="auto"
        />
      </div>
      <span slot="footer">
        <el-button @click="videoPreviewVisible = false">关闭</el-button>
        <el-button type="primary" @click="requestVideoFullscreen">全屏播放</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { MessageBox } from 'element-ui'
import {
  cancelCastingNoticeInvitation,
  createCastingNoticeInvitation,
  getAdminCastingNoticeDetail,
  getCastingNoticeApplicationDetail,
  getCastingNoticeApplications,
  getCastingNoticeArtistDetail,
  getCastingNoticeArtists,
  getCastingNoticeInvitations,
  validateCastingNoticeInvitation,
  updateAdminOrderFulfillment,
  updateAdminOrderSettlement,
  saveAdminMerchantOrderReview,
  approveAdminArtistCloseOrder,
  rejectAdminArtistCloseOrder,
  updateCastingNoticeApplication
} from '../../../api/castingNotice'
import { getPlatformOrderDetail, getPlatformOrders } from '../../../api/platformManage'
import { hasPermission } from '../../../utils/auth'
import {
  badgeValue,
  countPendingApplications,
  countPendingOrders,
  isPendingOrder
} from '../../../utils/recruitment-badges'
import { isImageMedia, isVideoMedia, maskMobile, membershipLevelLabel, normalizeMediaItem, resolveMediaUrl } from '../../../utils/format'

const PLATFORM_OFFICIAL_NAME = 'z7平台官方'

const applicationStatusOptions = [
  { label: '已报名', value: 1 },
  { label: '待筛选', value: 2 },
  { label: '已沟通', value: 3 },
  { label: '已邀约', value: 4 },
  { label: '待确认', value: 5 },
  { label: '已录用', value: 6 },
  { label: '已拒绝', value: 7 },
  { label: '已失效', value: 8 }
]

export default {
  name: 'CastingNoticeManagePage',
  data() {
    return {
      noticeId: this.$route.params.id,
      notice: {},
      activeTab: 'applications',
      applicationQuery: { applyStatus: undefined, keyword: '' },
      applications: [],
      applicationLoading: false,
      applicationDetailVisible: false,
      applicationDetailLoading: false,
      applicationDetail: {},
      applicationArtistDetail: {},
      artistQuery: { keyword: '' },
      artists: [],
      artistLoading: false,
      invitations: [],
      invitationLoading: false,
      inviteDialogVisible: false,
      inviteSubmitting: false,
      inviteForm: { artistId: null, artistName: '', invitationTitle: '', invitationContent: '' },
      orders: [],
      orderLoading: false,
      orderDetailVisible: false,
      orderDetailLoading: false,
      orderDetail: {},
      orderArtistDetail: {},
      closeRequestDialogVisible: false,
      closeRequestSubmitting: false,
      closeRequestForm: {
        orderId: null,
        orderNo: '',
        artistName: '',
        closeRequestReason: '',
        closeRequestAt: '',
        rejectReason: ''
      },
      settlementDialogVisible: false,
      settlementSubmitting: false,
      settlementForm: { orderId: null, orderNo: '', settlementAmount: '', remark: '' },
      reviewDialogVisible: false,
      reviewSubmitting: false,
      reviewForm: { orderId: null, orderNo: '', artistName: '', content: '' },
      videoPreviewVisible: false,
      previewVideo: {},
      applicationStatusOptions
    }
  },
  computed: {
    applicationProfile() {
      const detail = this.applicationDetail || {}
      const artistDetail = this.applicationArtistDetail || {}
      return Object.assign({}, detail, artistDetail, {
        mediaList: artistDetail.mediaList && artistDetail.mediaList.length ? artistDetail.mediaList : (detail.mediaList || []),
        historyRecords: artistDetail.historyRecords || [],
        starProfile: artistDetail.starProfile || {}
      })
    },
    applicationMediaList() {
      return (this.applicationProfile.mediaList || [])
        .map(item => normalizeMediaItem(typeof item === 'string' ? { mediaUrl: item } : item))
        .filter(Boolean)
    },
    applicationImageMediaList() {
      return this.applicationMediaList.filter(item => isImageMedia(item))
    },
    applicationVideoMediaList() {
      return this.applicationMediaList.filter(item => isVideoMedia(item))
    },
    applicationPreviewImages() {
      return this.applicationImageMediaList.map(item => item.mediaUrl).filter(Boolean)
    },
    applicationHistoryRecords() {
      return this.applicationProfile.historyRecords || []
    },
    applicationStarProfile() {
      return this.applicationProfile.starProfile || {}
    },
    applicationStarRecords() {
      return this.applicationStarProfile.records || []
    },
    applicationStarOrderStats() {
      return this.applicationStarProfile.orderStats || {}
    },
    applicationStarOrderStatItems() {
      return [
        { label: '累计接单', value: this.applicationStarOrderStats.totalOrderCount || 0 },
        { label: '进行中', value: this.applicationStarOrderStats.inProgressOrderCount || 0 },
        { label: '已完成', value: this.applicationStarOrderStats.completedOrderCount || 0 },
        { label: '已结算', value: this.applicationStarOrderStats.settledOrderCount || 0 },
        { label: '异常关闭', value: this.applicationStarOrderStats.closedOrderCount || 0 }
      ]
    },
    orderDetailMediaList() {
      return (this.orderDetail.mediaList || []).map(normalizeMediaItem).filter(Boolean)
    },
    orderDetailImageMediaList() {
      return this.orderDetailMediaList.filter(item => isImageMedia(item))
    },
    orderDetailVideoMediaList() {
      return this.orderDetailMediaList.filter(item => isVideoMedia(item))
    },
    orderDetailPreviewImages() {
      return this.orderDetailImageMediaList.map(item => item.mediaUrl).filter(Boolean)
    },
    orderLocationText() {
      const city = this.orderDetail.workCity || ''
      const area = this.orderDetail.workArea || ''
      if (!city && !area) return '-'
      return [city, area].filter(Boolean).join(' ')
    },
    orderMerchantDisplayName() {
      return this.merchantDisplayName(this.orderDetail)
    },
    orderSubjectMerchantName() {
      const name = this.cleanDisplayName(this.orderDetail.subjectMerchantName)
      return name || '-'
    },
    orderArtistProfile() {
      return Object.assign({}, this.orderDetail || {}, this.orderArtistDetail || {})
    },
    orderArtistMediaList() {
      return (this.orderArtistProfile.mediaList || [])
        .map(item => normalizeMediaItem(typeof item === 'string' ? { mediaUrl: item } : item))
        .filter(Boolean)
    },
    orderArtistImageMediaList() {
      return this.orderArtistMediaList.filter(item => isImageMedia(item) && !this.isArtistAvatarMedia(item))
    },
    orderArtistVideoMediaList() {
      return this.orderArtistMediaList.filter(item => isVideoMedia(item))
    },
    orderArtistPreviewImages() {
      return this.orderArtistImageMediaList.map(item => item.mediaUrl).filter(Boolean)
    },
    applicationBadgeCount() {
      return countPendingApplications(this.applications)
    },
    orderBadgeCount() {
      return countPendingOrders(this.orders)
    }
  },
  created() {
    if (!hasPermission('casting_notice:manage')) {
      this.$message.error('无通告招募管理权限')
      this.goBack()
      return
    }
    const routeTab = this.$route.query.tab
    if (routeTab && ['applications', 'invitations', 'orders'].includes(routeTab)) {
      this.activeTab = routeTab
    }
    this.bootstrap()
  },
  methods: {
    badgeValue,
    isPendingOrder,
    maskMobile,
    resolveMediaUrl,
    async bootstrap() {
      await this.loadNotice()
      await Promise.all([
        this.fetchApplications(),
        this.fetchInvitations(),
        this.fetchOrders()
      ])
      await this.handleTabChange({ name: this.activeTab })
    },
    async loadNotice() {
      this.notice = await getAdminCastingNoticeDetail(this.noticeId)
    },
    async handleTabChange(tab) {
      if (tab.name === 'applications') this.fetchApplications()
      if (tab.name === 'invitations') {
        await Promise.all([
          this.fetchApplications(),
          this.fetchOrders(),
          this.fetchInvitations(),
          this.fetchArtists()
        ])
        this.refreshArtistInviteEligibility()
      }
      if (tab.name === 'orders') this.fetchOrders()
    },
    async fetchApplications() {
      this.applicationLoading = true
      try {
        this.applications = await getCastingNoticeApplications(this.noticeId, {
          applyStatus: this.applicationQuery.applyStatus || undefined,
          keyword: this.applicationQuery.keyword || undefined
        })
      } finally {
        this.applicationLoading = false
      }
    },
    async fetchArtists() {
      this.artistLoading = true
      try {
        this.artists = await getCastingNoticeArtists(this.noticeId, {
          keyword: this.artistQuery.keyword || undefined
        })
        this.refreshArtistInviteEligibility()
      } finally {
        this.artistLoading = false
      }
    },
    async fetchInvitations() {
      this.invitationLoading = true
      try {
        this.invitations = await getCastingNoticeInvitations(this.noticeId, {})
      } finally {
        this.invitationLoading = false
      }
    },
    async fetchOrders() {
      this.orderLoading = true
      try {
        this.orders = await getPlatformOrders({ noticeId: this.noticeId })
      } finally {
        this.orderLoading = false
      }
    },
    applicationStatusLabel(status) {
      const item = applicationStatusOptions.find(option => option.value === Number(status))
      return item ? item.label : (status == null ? '-' : String(status))
    },
    invitationStatusLabel(status) {
      if (Number(status) === 1) return '待确认'
      if (Number(status) === 2) return '已接受'
      if (Number(status) === 3) return '已拒绝'
      if (Number(status) === 4 || Number(status) < 0) return '已撤回'
      if (Number(status) === 5) return '已过期'
      if (Number(status) === 8) return '已失效'
      return status == null ? '-' : String(status)
    },
    genderText(gender) {
      if (Number(gender) === 1) return '男'
      if (Number(gender) === 2) return '女'
      return '未知'
    },
    membershipText(row) {
      if (!row || row.membershipStatus !== 1) return '非会员'
      return membershipLevelLabel(row.membershipLevel, '非会员')
    },
    membershipTagType(row) {
      if (!row || row.membershipStatus !== 1) return 'info'
      return row.membershipLevel === 2 ? 'warning' : 'success'
    },
    membershipExpireText(row) {
      if (!row || row.membershipStatus !== 1) return '-'
      return row.membershipExpireAt || '永久有效'
    },
    canHandleApplication(row) {
      const status = Number(row.applyStatus)
      return status >= 1 && status <= 5
    },
    canSettle(row) {
      const fulfillment = Number(row.fulfillmentStatus)
      const settlement = Number(row.settlementStatus)
      return (fulfillment === 2 || fulfillment === 3) && settlement === 1
    },
    hasCloseRequest(row) {
      return !!(row && row.closeRequested)
    },
    async openCloseRequestDialog(row) {
      this.closeRequestForm = {
        orderId: row.id,
        orderNo: row.orderNo,
        artistName: row.artistName,
        closeRequestReason: row.closeRequestReason || '',
        closeRequestAt: row.closeRequestAt || '',
        rejectReason: ''
      }
      if (!this.closeRequestForm.closeRequestReason) {
        try {
          const detail = await getPlatformOrderDetail(row.id)
          this.closeRequestForm.closeRequestReason = (detail && detail.closeRequestReason) || ''
          this.closeRequestForm.closeRequestAt = (detail && detail.closeRequestAt) || this.closeRequestForm.closeRequestAt
        } catch (e) {
          // ignore
        }
      }
      this.closeRequestDialogVisible = true
    },
    resetCloseRequestDialog() {
      this.closeRequestSubmitting = false
      this.closeRequestForm = {
        orderId: null,
        orderNo: '',
        artistName: '',
        closeRequestReason: '',
        closeRequestAt: '',
        rejectReason: ''
      }
    },
    async approveCloseRequest() {
      if (!this.closeRequestForm.orderId) return
      try {
        await MessageBox.confirm('确认同意艺人异常关闭该订单吗？', '操作确认', { type: 'warning' })
      } catch (e) {
        return
      }
      this.closeRequestSubmitting = true
      try {
        await approveAdminArtistCloseOrder({
          orderId: this.closeRequestForm.orderId,
          closeReason: this.closeRequestForm.closeRequestReason || '商家同意艺人异常关闭申请'
        })
        this.$message.success('已同意异常关闭')
        this.closeRequestDialogVisible = false
        await this.fetchOrders()
      } finally {
        this.closeRequestSubmitting = false
      }
    },
    async rejectCloseRequest() {
      if (!this.closeRequestForm.orderId) return
      const rejectReason = (this.closeRequestForm.rejectReason || '').trim()
      if (!rejectReason) {
        this.$message.warning('请输入拒绝原因')
        return
      }
      this.closeRequestSubmitting = true
      try {
        await rejectAdminArtistCloseOrder({
          orderId: this.closeRequestForm.orderId,
          closeReason: rejectReason
        })
        this.$message.success('已拒绝异常关闭申请')
        this.closeRequestDialogVisible = false
        await this.fetchOrders()
      } finally {
        this.closeRequestSubmitting = false
      }
    },
    async openApplicationDetail(row) {
      this.applicationDetailVisible = true
      this.applicationDetailLoading = true
      this.applicationDetail = {}
      this.applicationArtistDetail = {}
      try {
        const detail = await getCastingNoticeApplicationDetail(row.id)
        this.applicationDetail = detail || {}
        const artistId = (detail && detail.artistId) || (row && row.artistId)
        if (artistId) {
          try {
            this.applicationArtistDetail = await getCastingNoticeArtistDetail(this.noticeId, artistId)
          } catch (e) {
            this.applicationArtistDetail = {}
          }
        }
      } catch (error) {
        this.applicationDetailVisible = false
        this.$message.error(error.message || '加载报名详情失败')
      } finally {
        this.applicationDetailLoading = false
      }
    },
    resetApplicationDetailDialog() {
      this.applicationDetailLoading = false
      this.applicationDetail = {}
      this.applicationArtistDetail = {}
    },
    async updateApplicationStatus(row, applyStatus) {
      const text = applyStatus === 6 ? '录用' : '拒绝'
      try {
        await MessageBox.confirm(`确认${text}该报名吗？`, '操作确认', { type: 'warning' })
      } catch (e) {
        return
      }
      await updateCastingNoticeApplication({ applicationId: row.id, applyStatus })
      this.$message.success(`${text}成功`)
      await this.fetchApplications()
      await this.fetchOrders()
    },
    openInviteDialog(row) {
      const artistId = String(row.artistId || row.id || '')
      if (!artistId) {
        this.$message.warning('艺人信息异常')
        return
      }
      this.refreshArtistInviteEligibility()
      const latest = (this.artists || []).find(item => String(item.artistId || item.id || '') === artistId) || row
      if (latest.canInvite !== true) {
        this.$message.warning(latest.inviteBlockMessage || '当前无法发起邀约')
        return
      }
      validateCastingNoticeInvitation(this.noticeId, artistId)
        .then(result => {
          if (!result || result.canInvite !== true) {
            this.$message.warning((result && result.message) || '当前无法发起邀约')
            return
          }
          this.inviteForm = {
            artistId,
            artistName: latest.artistName,
            invitationTitle: `${this.notice.title || '合作'}邀约`,
            invitationContent: ''
          }
          this.inviteDialogVisible = true
        })
        .catch(() => {})
    },
    refreshArtistInviteEligibility() {
      this.artists = (this.artists || []).map(row => {
        const artistId = String(row.artistId || row.id || '')
        const localReason = artistId ? this.resolveLocalInviteBlockReason(artistId) : '艺人信息异常'
        if (localReason || row.canInvite === false) {
          return {
            ...row,
            canInvite: false,
            inviteBlockMessage: localReason || row.inviteBlockMessage || '当前无法发起邀约'
          }
        }
        return {
          ...row,
          canInvite: row.canInvite === true,
          inviteBlockMessage: row.inviteBlockMessage || null
        }
      })
    },
    resolveLocalInviteBlockReason(artistId) {
      const artistKey = String(artistId)
      const applied = (this.applications || []).some(item => {
        const itemArtistId = String(item.artistId || '')
        if (itemArtistId && itemArtistId === artistKey) {
          return Number(item.applyStatus) !== 8
        }
        return String(item.artistName || '') === String(this.findArtistName(artistKey) || '')
          && Number(item.applyStatus) !== 8
      })
      if (applied) {
        return '该艺人已报名此通告，无法发起邀约'
      }
      const invited = (this.invitations || []).some(item => {
        if (String(item.artistId || '') !== artistKey) return false
        const status = Number(item.status)
        return status === 1 || status === 2
      })
      if (invited) {
        return '该艺人已有待确认或已接受邀约，无法重复发起'
      }
      const blockedOrder = (this.orders || []).find(item => {
        const profileId = String(item.artistProfileId || item.artistId || '')
        if (profileId && profileId === artistKey) {
          if (item.closed === true || Number(item.fulfillmentStatus) === 4) return false
          return true
        }
        const artistName = this.findArtistName(artistKey)
        return artistName && String(item.artistName || '') === artistName
          && item.closed !== true
          && Number(item.fulfillmentStatus) !== 4
      })
      if (blockedOrder) {
        if (Number(blockedOrder.fulfillmentStatus) === 3) {
          return '该通告与艺人存在已完成的订单，无法发起邀约'
        }
        return '该通告与艺人存在进行中的订单，无法发起邀约'
      }
      return ''
    },
    findArtistName(artistId) {
      const artistKey = String(artistId || '')
      const row = (this.artists || []).find(item => String(item.artistId || item.id || '') === artistKey)
      return row ? row.artistName : ''
    },
    resetInviteDialog() {
      this.inviteForm = { artistId: null, artistName: '', invitationTitle: '', invitationContent: '' }
    },
    async submitInvitation() {
      if (!this.inviteForm.invitationTitle.trim()) {
        this.$message.warning('请填写邀约标题')
        return
      }
      const localReason = this.resolveLocalInviteBlockReason(this.inviteForm.artistId)
      if (localReason) {
        this.$message.warning(localReason)
        return
      }
      this.inviteSubmitting = true
      try {
        const check = await validateCastingNoticeInvitation(this.noticeId, this.inviteForm.artistId)
        if (!check || check.canInvite !== true) {
          this.$message.warning((check && check.message) || '当前无法发起邀约')
          return
        }
        await createCastingNoticeInvitation({
          noticeId: this.noticeId,
          artistId: this.inviteForm.artistId,
          invitationTitle: this.inviteForm.invitationTitle.trim(),
          invitationContent: this.inviteForm.invitationContent || undefined
        })
        this.$message.success('邀约已发送')
        this.inviteDialogVisible = false
        await Promise.all([this.fetchInvitations(), this.fetchArtists(), this.fetchOrders()])
        this.refreshArtistInviteEligibility()
      } finally {
        this.inviteSubmitting = false
      }
    },
    async cancelInvitation(row) {
      try {
        await MessageBox.confirm('确认撤回该邀约吗？', '操作确认', { type: 'warning' })
      } catch (e) {
        return
      }
      await cancelCastingNoticeInvitation(this.noticeId, row.id)
      this.$message.success('已撤回')
      await Promise.all([this.fetchInvitations(), this.fetchArtists(), this.fetchOrders()])
      this.refreshArtistInviteEligibility()
    },
    async openOrderDetail(row) {
      this.orderDetailVisible = true
      this.orderDetailLoading = true
      this.orderDetail = {}
      this.orderArtistDetail = {}
      try {
        const detail = await getPlatformOrderDetail(row.id)
        this.orderDetail = detail || {}
        const artistId = (detail && (detail.artistId || detail.artistProfileId)) || (row && (row.artistId || row.artistProfileId))
        if (artistId) {
          try {
            this.orderArtistDetail = await getCastingNoticeArtistDetail(this.noticeId, artistId)
          } catch (e) {
            this.orderArtistDetail = {}
          }
        }
      } catch (error) {
        this.orderDetailVisible = false
        this.$message.error(error.message || '加载订单详情失败')
      } finally {
        this.orderDetailLoading = false
      }
    },
    resetOrderDetailDialog() {
      this.orderDetailLoading = false
      this.orderDetail = {}
      this.orderArtistDetail = {}
    },
    isArtistAvatarMedia(item) {
      if (!item) return false
      return Number(item.mediaType) === 1
    },
    merchantDisplayName(row) {
      const name = row && row.merchantName != null ? String(row.merchantName).trim() : ''
      if (!name) return '-'
      return this.isPlaceholderName(name) ? PLATFORM_OFFICIAL_NAME : name
    },
    cleanDisplayName(value) {
      const name = value != null ? String(value).trim() : ''
      if (!name || this.isPlaceholderName(name)) return ''
      return name
    },
    isPlaceholderName(value) {
      return /^\?+$/.test(String(value || '').trim())
    },
    mediaTagType(mediaType) {
      if (String(mediaType).includes('视频')) return 'warning'
      if (String(mediaType).includes('待验收')) return 'success'
      return 'primary'
    },
    async updateFulfillment(row, fulfillmentStatus) {
      await updateAdminOrderFulfillment({ orderId: row.id, fulfillmentStatus })
      this.$message.success('履约状态已更新')
      await this.fetchOrders()
    },
    openSettlementDialog(row) {
      this.settlementForm = {
        orderId: row.id,
        orderNo: row.orderNo,
        settlementAmount: '',
        remark: ''
      }
      this.settlementDialogVisible = true
    },
    resetSettlementDialog() {
      this.settlementForm = { orderId: null, orderNo: '', settlementAmount: '', remark: '' }
    },
    async submitSettlement() {
      const amount = Number(this.settlementForm.settlementAmount)
      if (!amount || amount <= 0) {
        this.$message.warning('请输入有效结算金额')
        return
      }
      this.settlementSubmitting = true
      try {
        await updateAdminOrderSettlement({
          orderId: this.settlementForm.orderId,
          settlementStatus: 2,
          settlementAmount: amount,
          remark: this.settlementForm.remark || undefined
        })
        this.$message.success('结算成功')
        this.settlementDialogVisible = false
        await this.fetchOrders()
      } finally {
        this.settlementSubmitting = false
      }
    },
    openReviewDialog(row) {
      this.reviewForm = {
        orderId: row.id,
        orderNo: row.orderNo,
        artistName: row.artistName,
        content: ''
      }
      this.reviewDialogVisible = true
    },
    resetReviewDialog() {
      this.reviewSubmitting = false
      this.reviewForm = { orderId: null, orderNo: '', artistName: '', content: '' }
    },
    async submitReview() {
      const content = (this.reviewForm.content || '').trim()
      if (!content) {
        this.$message.warning('请输入评价内容')
        return
      }
      if (!this.reviewForm.orderId) return
      this.reviewSubmitting = true
      try {
        await saveAdminMerchantOrderReview({
          orderId: this.reviewForm.orderId,
          content
        })
        this.$message.success('评价成功')
        this.reviewDialogVisible = false
        await this.fetchOrders()
      } finally {
        this.reviewSubmitting = false
      }
    },
    openVideoPreview(item) {
      this.previewVideo = {
        mediaUrl: item.mediaUrl || '',
        coverUrl: item.coverUrl || ''
      }
      this.videoPreviewVisible = true
    },
    handleVideoPreviewClosed() {
      const player = this.$refs.previewVideoPlayer
      if (player && typeof player.pause === 'function') {
        player.pause()
        player.currentTime = 0
      }
      this.previewVideo = {}
    },
    async requestVideoFullscreen() {
      const player = this.$refs.previewVideoPlayer
      if (!player) return
      const requestFullscreen =
        player.requestFullscreen ||
        player.webkitRequestFullscreen ||
        player.mozRequestFullScreen ||
        player.msRequestFullscreen
      if (!requestFullscreen) {
        this.$message.warning('当前浏览器不支持全屏播放')
        return
      }
      await requestFullscreen.call(player)
    },
    normalizeStarAttachment(item) {
      if (!item) return null
      return normalizeMediaItem(typeof item === 'string' ? { mediaUrl: item } : item)
    },
    starAttachmentItems(row) {
      return ((row && row.attachments) || [])
        .map(item => this.normalizeStarAttachment(item))
        .filter(Boolean)
    },
    starImagePreviewList(row) {
      return this.starAttachmentItems(row)
        .filter(item => this.isImageAttachment(item))
        .map(item => item.mediaUrl)
    },
    isImageAttachment(item) {
      return isImageMedia(item)
    },
    isVideoAttachment(item) {
      return isVideoMedia(item)
    },
    recordTypeText(type) {
      if (Number(type) === 1) return '成长记录'
      if (Number(type) === 2) return '获奖'
      if (Number(type) === 3) return '合作经历'
      return '其他'
    },
    starRecordDesc(row) {
      if (!row) return '-'
      return row.recordDesc || row.achievementDesc || '-'
    },
    historyStatusText(row) {
      if (!row) return '-'
      if (row.recordType === '邀约') return this.invitationStatusLabel(row.status)
      return this.applicationStatusLabel(row.status)
    },
    goDetail() {
      this.$router.push({
        path: `/audits/casting-notice/${this.noticeId}`,
        query: { from: 'hall', fromStatus: '5' }
      })
    },
    goBack() {
      this.$router.push('/casting/hall')
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.page-subtitle {
  margin-top: 6px;
  color: #909399;
  font-size: 13px;
}

.toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.section-title {
  margin: 20px 0 12px;
  font-weight: 600;
}

.order-detail-wrap {
  max-height: 72vh;
  overflow-y: auto;
  padding-right: 6px;
  min-height: 120px;
}

.detail-section {
  margin-bottom: 18px;
}

.detail-media-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-media-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #606266;
  font-size: 12px;
}

.media-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.log-item-title {
  font-weight: 600;
  color: #303133;
}

.log-item-desc {
  margin-top: 4px;
  color: #606266;
}

.review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.review-card {
  min-height: 160px;
  padding: 16px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
}

.review-title {
  margin-bottom: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.review-meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  color: #909399;
  font-size: 12px;
}

.review-content {
  color: #606266;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
}

.dialog-section-title {
  margin-top: 0;
}

.section-table {
  margin-bottom: 8px;
}

.application-detail-dialog {
  min-height: 240px;
}

.artist-summary {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

.artist-avatar-large {
  width: 88px;
  height: 88px;
  border-radius: 16px;
  background: #f5f7fa;
  flex-shrink: 0;
}

.artist-avatar-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #909399;
  font-size: 14px;
}

.artist-summary-name {
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}

.artist-summary-sub {
  margin-top: 4px;
  color: #909399;
}

.artist-summary-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.video-grid {
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
}

.media-image {
  width: 100%;
  height: 150px;
  border-radius: 8px;
  background: #f5f7fa;
}

.video-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.media-video {
  width: 100%;
  height: 180px;
  display: block;
  border-radius: 8px;
  background: #111827;
  object-fit: contain;
}

.video-actions {
  display: flex;
  justify-content: flex-end;
}

.history-table,
.star-table {
  margin-top: 12px;
}

.video-preview-wrap {
  display: flex;
  justify-content: center;
}

.preview-video-player {
  width: 100%;
  max-height: 72vh;
  border-radius: 10px;
  background: #111827;
}

.stat-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.stat-item {
  padding: 14px 10px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
  text-align: center;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}

.stat-label {
  margin-top: 6px;
  color: #909399;
}

.star-attachment-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.star-attachment-item {
  width: 64px;
  height: 64px;
}

.star-attachment-thumb,
.star-attachment-video-card {
  width: 64px;
  height: 64px;
  border-radius: 6px;
}

.star-attachment-thumb {
  background: #f5f7fa;
}

.star-attachment-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 12px;
  text-align: center;
  background: #f5f7fa;
}

.star-attachment-video-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: #111827;
}

.star-attachment-video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.star-attachment-video-mask {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #fff;
  font-size: 12px;
  background: rgba(17, 24, 39, 0.42);
}

.star-attachment-video-play {
  width: 24px;
  height: 24px;
  line-height: 24px;
  border-radius: 50%;
  text-align: center;
  background: rgba(255, 255, 255, 0.22);
}

.star-attachment-file {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #f5f7fa;
}

.danger-link {
  color: #f56c6c;
}

.invite-disabled-link {
  color: #c0c4cc;
  cursor: not-allowed;
  font-size: 14px;
}

.close-request-reject-form {
  margin-top: 16px;
}

.tab-label-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.tab-badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #f56c6c;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  font-weight: 600;
  box-sizing: border-box;
}

.order-no-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.order-row-badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #f56c6c;
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  font-weight: 600;
  box-sizing: border-box;
  flex-shrink: 0;
}
</style>
